import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import { AccessTime, CalendarMonth, EscalatorWarning, Forum, Person, StickyNote2 } from '@mui/icons-material'
import moment from 'moment/moment'
import styles from './showStaffWorkDialog.module.scss'
import { Daycare } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Work } from '@/types/work'
import DeleteStaffWorkDialog from '../../staffWork/deleteStaffWork/deleteStaffWorkDialog'
import EditOrCreateWorkDialog from '../../staffWork/editOrCreateStaffWork/editOrCreateWorkDialog'
import { StaffWithLinks } from '@/types/Staff'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { StaffPreparationTask } from '@/types/StaffPreparationTask'
import {
    getPreparationTimeAfterRange,
    getPreparationTimeBeforeRange,
    TimeRange
} from '@/modules/helper/workPreparationTimeHelper'
import { isPastDate, titleWithPastLabel } from '@/modules/helper/editionLimitHelper'

interface ShowStaffWorkDialogProps {
    open: boolean
    handleClose: () => void
    staffWork: Work
    daycare: Daycare
    works: Array<Work>
    staffsListData: Array<StaffWithLinks>
    refetchData: () => void
    canEditStaffWorks: boolean
}

const ShowStaffWorkDialog: FunctionComponent<ShowStaffWorkDialogProps> = ({
    open,
    handleClose,
    staffWork,
    daycare,
    works,
    staffsListData,
    refetchData,
    canEditStaffWorks
}: ShowStaffWorkDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [isDeleteStaffWorkDialogOpen, setDeleteStaffWorkDialogOpen] = useState(false)

    const [isEditStaffWorkDialogOpen, setEditStaffWorkDialogOpen] = useState(false)

    const preparationBeforeRange: TimeRange | null = staffWork.preparationTimeBefore
        ? getPreparationTimeBeforeRange(moment(staffWork.start, 'HH:mm:ss').toDate(), staffWork.preparationTimeBefore)
        : null
    const preparationAfterRange: TimeRange | null = staffWork.preparationTimeAfter
        ? getPreparationTimeAfterRange(moment(staffWork.end, 'HH:mm:ss').toDate(), staffWork.preparationTimeAfter)
        : null

    const isPast: boolean = isPastDate(staffWork.date)

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader title={titleWithPastLabel(t(staffWork.type), isPast, t)} />
                <DialogBody className={styles.DialogBody}>
                    <div className={styles.Container}>
                        <CalendarMonth className={styles.Icon} />
                        <div className={styles.TextContainer}>
                            <p className={styles.FirstText}>
                                {format(parse(staffWork.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                    locale: getLocalFromLanguage()
                                })}
                            </p>
                            <p className={styles.SecondText}>
                                {t('from') +
                                    ' ' +
                                    moment(staffWork.start, 'HH:mm:ss').format('HH:mm') +
                                    ' ' +
                                    t('to').toLowerCase() +
                                    ' ' +
                                    moment(staffWork.end, 'HH:mm:ss').format('HH:mm') +
                                    ' ' +
                                    t(staffWork.type).toLocaleLowerCase()}
                            </p>
                        </div>
                    </div>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.SecondContainer}>
                        <Person
                            className={styles.ClickableIcon}
                            onClick={() =>
                                user?.currentDaycareRights.canReadStaffRights &&
                                navigate(`/staff?profile=${staffWork.staff}`)
                            }
                        />
                        <p className={styles.Text}>{staffWork.staffName}</p>
                    </div>
                    {staffWork.sectionGroupName && (
                        <div className={styles.SecondContainer}>
                            <EscalatorWarning className={styles.Icon} />
                            <p className={styles.Text}>{staffWork.sectionGroupName}</p>
                        </div>
                    )}
                    {preparationBeforeRange && (
                        <div className={styles.SecondContainer}>
                            <AccessTime className={styles.Icon} />
                            <p className={styles.Text}>
                                {t('preparationTasksBefore')} : {preparationBeforeRange.start} -{' '}
                                {preparationBeforeRange.end}
                                {staffWork.preparationTasksBefore && staffWork.preparationTasksBefore.length > 0
                                    ? ` (${staffWork.preparationTasksBefore
                                          .map((task: StaffPreparationTask) => task.name)
                                          .join(', ')})`
                                    : ''}
                            </p>
                        </div>
                    )}
                    {preparationAfterRange && (
                        <div className={styles.SecondContainer}>
                            <AccessTime className={styles.Icon} />
                            <p className={styles.Text}>
                                {t('preparationTasksAfter')} : {preparationAfterRange.start} -{' '}
                                {preparationAfterRange.end}
                                {staffWork.preparationTasksAfter && staffWork.preparationTasksAfter.length > 0
                                    ? ` (${staffWork.preparationTasksAfter
                                          .map((task: StaffPreparationTask) => task.name)
                                          .join(', ')})`
                                    : ''}
                            </p>
                        </div>
                    )}
                    {staffWork.internalNote && (
                        <div className={styles.SecondContainer}>
                            <StickyNote2 className={styles.Icon} />
                            <p className={styles.Text}>{staffWork.internalNote}</p>
                        </div>
                    )}
                    {staffWork.externalNote && (
                        <div className={styles.SecondContainer}>
                            <Forum className={styles.Icon} />
                            <p className={styles.Text}>{staffWork.externalNote}</p>
                        </div>
                    )}
                    <div className={styles.ButtonContainer}>
                        {canEditStaffWorks ? (
                            <>
                                <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                                <ButtonTextIcon
                                    type={'delete'}
                                    onClick={() => setDeleteStaffWorkDialogOpen(true)}
                                    className={styles.Button}
                                />
                                <ButtonTextIcon
                                    type={'edit'}
                                    onClick={() => setEditStaffWorkDialogOpen(true)}
                                    className={styles.Button}
                                />
                            </>
                        ) : (
                            <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.ButtonFullWidth} />
                        )}
                    </div>
                </DialogBody>
            </Dialog>
            {canEditStaffWorks && isDeleteStaffWorkDialogOpen && (
                <DeleteStaffWorkDialog
                    open={isDeleteStaffWorkDialogOpen}
                    handleClose={() => setDeleteStaffWorkDialogOpen(false)}
                    workId={staffWork.id}
                    daycareId={daycare.id}
                    refetch={refetchData}
                    closeParentDialog={() => handleClose()}
                />
            )}
            {canEditStaffWorks && isEditStaffWorkDialogOpen && (
                <EditOrCreateWorkDialog
                    open={isEditStaffWorkDialogOpen}
                    staffsListData={staffsListData}
                    handleClose={() => setEditStaffWorkDialogOpen(false)}
                    daycare={daycare}
                    date={moment(staffWork.date, 'DD-MM-YYYY').format('DD-MM-YYYY')}
                    from={moment(staffWork.start, 'HH:mm:ss').format('HH:mm')}
                    to={moment(staffWork.end, 'HH:mm:ss').format('HH:mm')}
                    work={staffWork}
                    works={works}
                    refetch={refetchData}
                    closeParentDialog={() => handleClose()}
                />
            )}
        </>
    )
}

export default ShowStaffWorkDialog
